import React, { useEffect, useState } from "react";

import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import { useDispatch } from "react-redux";

// @ts-ignore - JS Redux slice

import { updateSource } from "helpers/Sources/updateSource";

import SwalAlertComp from "components/SwalAlert";

import { updateSourceInList } from "../../../../../../../redux/slices/sources/sources.slice";
import { getSingleTrackerRedux } from "../../../../../../../redux/slices/trackers/trackers.asyncActions";
import { TabProps } from "../../../SourceModal.types";

const isValidUrl = (value: string): boolean => {
    try {
        const url = new URL(value.trim());
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
};

const DOMAIN_REGEX =
    /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

const isValidDomain = (value: string): boolean =>
    DOMAIN_REGEX.test(value.trim());

const UpdateTab: React.FC<TabProps> = ({
    source,
    tracker,
    language,
    onClose,
}) => {
    const dispatch = useDispatch();
    const [updateDomain, setUpdateDomain] = useState(
        source?.domain ?? (source?.name || ""),
    );
    const [isChecked, setIsChecked] = useState(source?.is_generic === 1);
    const [updateHostDomain, setUpdateHostDomain] = useState("");
    const [form, setForm] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);
    const [domainError, setDomainError] = useState("");
    const [subdomainError, setSubdomainError] = useState("");
    const [hostDomainError, setHostDomainError] = useState("");
    // Parse host for custom domain
    useEffect(() => {
        if (source?.host && !source?.is_generic) {
            const hostParts = source.host.split(".");
            if (hostParts.length > 1) {
                setForm(hostParts[0]);
                setUpdateHostDomain(hostParts.slice(1).join("."));
            }
        }
    }, [source]);

    const handleUpdateSource = async () => {
        if (!source?.id) return;

        let hasError = false;
        if (!updateDomain.trim()) {
            setDomainError("Domain is required");
            hasError = true;
        } else if (!isValidUrl(updateDomain)) {
            setDomainError("Enter a valid URL (e.g. https://www.example.com)");
            hasError = true;
        }
        if (!isChecked) {
            if (!form.trim()) {
                setSubdomainError("Subdomain is required");
                hasError = true;
            }
            if (!updateHostDomain.trim()) {
                setHostDomainError("Host domain is required");
                hasError = true;
            } else if (!isValidDomain(updateHostDomain)) {
                setHostDomainError("Enter a valid domain (e.g. example.com)");
                hasError = true;
            }
        }
        if (hasError) return;

        setIsUpdating(true);
        try {
            let updateData;
            if (isChecked) {
                updateData = {
                    domain: updateDomain,
                    host: "cpi.ssevt.com",
                    is_generic: isChecked ? 1 : 0,
                };
            } else {
                updateData = {
                    domain: updateDomain,
                    host: `${form}.${updateHostDomain}`,
                    is_generic: isChecked ? 1 : 0,
                };
            }

            const response = await updateSource(source.id, updateData);
            if (response.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: response.message,
                    icon: "success",
                    timer: 3000,
                });
                dispatch(
                    updateSourceInList({ id: source.id, changes: updateData }),
                );
                if (tracker?.tracker?.id) {
                    dispatch(getSingleTrackerRedux(tracker.tracker.id));
                }
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message,
                    icon: "error",
                });
            }
        } catch (error) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: "An error occurred while updating the source",
                icon: "error",
            });
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {language?.source_modal?.update_source ?? "Update Source"}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Update your source domain and host configuration.
            </Typography>

            <Stack spacing={3}>
                <Box>
                    <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                    >
                        {language?.source_modal?.domain ?? "Domain"}
                    </Typography>
                    <TextField
                        fullWidth
                        value={updateDomain}
                        onChange={(e) => {
                            setUpdateDomain(e.target.value);
                            if (domainError) setDomainError("");
                        }}
                        placeholder="https://www.example.com"
                        disabled={isUpdating}
                        size="small"
                        error={!!domainError}
                        helperText={domainError}
                    />
                </Box>

                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isChecked}
                                onChange={(e) => setIsChecked(e.target.checked)}
                                disabled={isUpdating}
                            />
                        }
                        label={
                            language?.source_modal?.use_generic_domain ??
                            "Use generic domain"
                        }
                    />
                </Box>

                {!isChecked && (
                    <Box>
                        <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                        >
                            {language?.source_modal?.signal_host_name ??
                                "Signal Host Name"}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <TextField
                                value={form}
                                onChange={(e) => {
                                    setForm(e.target.value);
                                    if (subdomainError) setSubdomainError("");
                                }}
                                placeholder="subdomain"
                                disabled={isUpdating}
                                size="small"
                                sx={{ flex: 1 }}
                                error={!!subdomainError}
                                helperText={subdomainError}
                            />
                            <TextField
                                value={updateHostDomain}
                                onChange={(e) => {
                                    setUpdateHostDomain(e.target.value);
                                    if (hostDomainError) setHostDomainError("");
                                }}
                                placeholder="example.com"
                                disabled={isUpdating}
                                size="small"
                                sx={{ flex: 2 }}
                                error={!!hostDomainError}
                                helperText={hostDomainError}
                            />
                        </Box>
                    </Box>
                )}

                <Box
                    sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUpdateSource}
                        disabled={isUpdating}
                        startIcon={
                            isUpdating ? <CircularProgress size={16} /> : null
                        }
                        sx={{ minWidth: 120 }}
                    >
                        {isUpdating
                            ? "Updating..."
                            : (language?.buttons?.update_source ??
                              "Update Source")}
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
};

export default UpdateTab;
